import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import Sidebar from "./components/Sidebar";
import CameraLoader from "./components/CameraLoader";
import { useUploadManager } from "./components/UploadManager";
import FolderManager from "./components/FolderManager";
import EventAnalytics from "./EventAnalytics";
import ErrorBoundary from "./components/ErrorBoundary";
import PeopleTab from "./PeopleTab";
import API_BASE_URL from "./config";
import { downloadFromApi } from "./desktop/downloadFromApi";
import "./EventDetails.css";
import publicOrigin from "./publicOrigin";
import { GALLERY_THEMES } from "./galleryThemes";
import { clearAuthStorage } from "./auth/logout";

const IMAGES_PAGE_SIZE = 60;

function EventDetails() {
  const navigate = useNavigate();
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [images, setImages] = useState([]);
  const [totalImages, setTotalImages] = useState(0);
  const [hasMoreImages, setHasMoreImages] = useState(false);
  const [loadingMoreImages, setLoadingMoreImages] = useState(false);
  const seenImageIdsRef = useRef(new Set());
  const loadMoreSentinelRef = useRef(null);
  const thumbGridRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const [shareLink, setShareLink] = useState("");
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const [liveShareLink, setLiveShareLink] = useState("");
  const [showLiveShareModal, setShowLiveShareModal] = useState(false);
  const [liveShareLoading, setLiveShareLoading] = useState(false);
  const qrGalleryIdRef = useRef(null);
  const [voteFilter, setVoteFilter] = useState("all");
  const [votedImages, setVotedImages] = useState([]);
  const [selectedImages, setSelectedImages] = useState([]);
  const [unselectedImages, setUnselectedImages] = useState([]);
  const [openFolder, setOpenFolder] = useState(null); // 'selected' | 'unselected' | null
  const [downloadingFolder, setDownloadingFolder] = useState(null);
  const [downloadProgress, setDownloadProgress] = useState(null);
  // Which upload folders (General/Candid/Traditional/...) to include when
  // downloading Selected/Not-selected as a ZIP — unchecked = excluded. Empty by
  // default so every folder is included until the user unchecks one.
  const [downloadExcludedFolders, setDownloadExcludedFolders] = useState({ selected: new Set(), unselected: new Set() });
  const [selectionLimitInput, setSelectionLimitInput] = useState("");
  const [galleryTheme, setGalleryTheme] = useState("magazine");
  const [savingTheme, setSavingTheme] = useState(false);
  const [savingLimit, setSavingLimit] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const [lightboxSource, setLightboxSource] = useState(null);
  // Photos whose RAW original never finished uploading (dropped connection,
  // closed tab). The gallery looks fine, so this is the only surfacing of it.
  const [missingRawBackups, setMissingRawBackups] = useState([]);
  // Photos the studio deliberately uploaded as JPEG only — informational, not a
  // failure, and styled differently so the two are never confused.
  const [jpegOnlyRaws, setJpegOnlyRaws] = useState([]);
  const [restoringRaw, setRestoringRaw] = useState(false);
  const restoreRawInputRef = useRef(null);

  // Upload folders (e.g. "Candid", "Traditional") — organizational only, the
  // photo quota is always counted across the whole event, never per folder.
  const [folders, setFolders] = useState([]);
  const [folderFilter, setFolderFilter] = useState("all"); // 'all' | 'general' | <folderId>
  const wasUploadingRef = useRef({});

  const uploadManager = useUploadManager();

  // Video states
  const [videos, setVideos] = useState([]);
  const [videoUploading, setVideoUploading] = useState(false);
  const [videoUploadProgress, setVideoUploadProgress] = useState(0);
  const [copiedVideoLink, setCopiedVideoLink] = useState(null);
  const videoPollTimers = useRef({});

  // PDF states
  const [pdfInfo, setPdfInfo] = useState(null);
  const [contractInfo, setContractInfo] = useState(null);
  const [pdfUploading, setPdfUploading] = useState(false);
  const [pdfUploadProgress, setPdfUploadProgress] = useState(0);
  const [pdfSubmissions, setPdfSubmissions] = useState([]);
  const [showPdfSubmissions, setShowPdfSubmissions] = useState(false);
  const [pdfReviewLink, setPdfReviewLink] = useState("");

  const userId = localStorage.getItem("userId");
  const [isProMax, setIsProMax] = useState(false);

  // Fast event meta (no photos) — the photo grid loads separately, page by page.
  const fetchEventDetails = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/events/single/${eventId}`);
      if (!response.ok) {
        setError("Event not found");
        return;
      }
      const data = await response.json();
      setEvent(data.event);
      setSelectionLimitInput(data.event.selection_limit != null ? String(data.event.selection_limit) : "");
      // Events created before themes existed have no value; they get the
      // magazine they have been showing all along.
      setGalleryTheme(data.event.gallery_theme === "grid" ? "grid" : "magazine");
    } catch (err) {
      setError("Failed to load event details");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchFolders = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/events/${eventId}/folders`);
      const data = await res.json();
      setFolders(data.folders || []);
    } catch { /* ignore */ }
  }, [eventId]);

  const fetchMissingRawBackups = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/events/${eventId}/missing-raw-backups`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setMissingRawBackups(data.missing || []);
      setJpegOnlyRaws(data.jpegOnly || []);
    } catch { /* ignore */ }
  }, [eventId]);

  // Re-upload originals for photos whose backup was interrupted. The studio
  // re-selects the RAW files off disk; we match them to the missing photos by
  // filename so they can hand us the whole folder and let us pick.
  const handleRestoreRawBackups = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;

    const byName = new Map(files.map((f) => [f.name.toLowerCase(), f]));
    const matched = missingRawBackups
      .map((m) => ({ ...m, file: byName.get((m.original_filename || "").toLowerCase()) }))
      .filter((m) => m.file);

    if (matched.length === 0) {
      setError("None of those files match the photos missing a backup — pick the original RAW files by their original names.");
      setTimeout(() => setError(""), 5000);
      return;
    }

    setRestoringRaw(true);
    let ok = 0;
    for (const m of matched) {
      try {
        const res = await fetch(`${API_BASE_URL}/api/images/${m.id}/raw-upload`, {
          method: "POST",
          headers: {
            "Content-Type": "application/octet-stream",
            "X-Raw-Filename": m.file.name,
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: m.file,
        });
        if (res.ok) ok += 1;
      } catch { /* keep going; the banner will still list whatever is left */ }
    }
    setRestoringRaw(false);
    await fetchMissingRawBackups();
    if (ok > 0) {
      setSuccess(`Restored ${ok} RAW original${ok > 1 ? "s" : ""}.`);
      setTimeout(() => setSuccess(""), 4000);
    } else {
      setError("Could not restore those RAW originals — please try again.");
      setTimeout(() => setError(""), 5000);
    }
  };

  // Lazy-loaded photo grid: fetch one page of photos at a time instead of the
  // whole gallery up front, so events with hundreds/thousands of photos still
  // open quickly. folderFilter narrows to one upload folder ('general' or a
  // folder id); 'all' (default) spans every folder, same as before folders existed.
  const fetchImagesPage = useCallback(async (offset, folder) => {
    try {
      const folderParam = folder && folder !== "all" ? `&folderId=${folder}` : "";
      const response = await fetch(
        `${API_BASE_URL}/api/events/${eventId}/images?limit=${IMAGES_PAGE_SIZE}&offset=${offset}${folderParam}`
      );
      const data = await response.json();
      if (!response.ok) return;

      setImages((prev) => {
        const existingIds = new Set(prev.map((img) => img.id));
        const fresh = (data.images || []).filter((img) => !existingIds.has(img.id));
        fresh.forEach((img) => seenImageIdsRef.current.add(img.id));
        return [...prev, ...fresh];
      });
      setTotalImages(data.totalCount || 0);
      setHasMoreImages(!!data.hasMore);
    } catch (err) {
      console.error("Failed to load photos:", err);
    }
  }, [eventId]);

  const loadMoreImages = useCallback(async () => {
    if (loadingMoreImages || !hasMoreImages) return;
    setLoadingMoreImages(true);
    await fetchImagesPage(images.length, folderFilter);
    setLoadingMoreImages(false);
  }, [loadingMoreImages, hasMoreImages, images.length, folderFilter, fetchImagesPage]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (userId && token) {
      fetch(`${API_BASE_URL}/api/users/${userId}/storage-usage`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((r) => r.json())
        .then((d) => setIsProMax(d.subscription_type === "pro_max_ultra"))
        .catch(() => {});
    }
  }, [userId]);

  useEffect(() => {
    fetchEventDetails();
    fetchFolders();
    fetchVideos();
    fetchPdf();
    fetchContract();
    fetchMissingRawBackups();

    return () => {
      Object.values(videoPollTimers.current).forEach(t => clearTimeout(t));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  // Re-fetch the gallery (from the top) whenever the event or the folder filter changes.
  useEffect(() => {
    setImages([]);
    seenImageIdsRef.current = new Set();
    fetchImagesPage(0, folderFilter);
  }, [eventId, folderFilter, fetchImagesPage]);

  // Infinite scroll: load the next page as the user nears the bottom of the grid.
  useEffect(() => {
    const sentinel = loadMoreSentinelRef.current;
    const root = thumbGridRef.current;
    if (!sentinel || !root) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMoreImages();
      },
      { root, rootMargin: "200px" }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadMoreImages]);

  // As the global upload manager reports newly-processed photos for this event
  // (in any folder that matches the current filter), merge them into the grid
  // in real time, deduped against what's already shown.
  useEffect(() => {
    const relevant = Object.values(uploadManager.uploads).filter(
      (u) => u && String(u.eventId) === String(eventId)
    );
    const fresh = [];
    relevant.forEach((u) => {
      const uFolderKey = u.folderId == null ? "general" : String(u.folderId);
      if (folderFilter !== "all" && folderFilter !== uFolderKey) return;
      (u.completedImages || []).forEach((img) => {
        if (!seenImageIdsRef.current.has(img.id)) fresh.push(img);
      });
    });
    if (fresh.length === 0) return;

    fresh.forEach((img) => seenImageIdsRef.current.add(img.id));
    setImages((prev) => [...fresh, ...prev]);
    setTotalImages((prev) => prev + fresh.length);
  }, [uploadManager.uploads, eventId, folderFilter]);

  // Once any folder's upload for this event finishes, refresh event meta (total
  // photo count) and folder counts so they don't go stale.
  useEffect(() => {
    let finished = false;
    Object.entries(uploadManager.uploads).forEach(([key, u]) => {
      if (!u || String(u.eventId) !== String(eventId)) return;
      const was = wasUploadingRef.current[key];
      const now = !!u.isUploading;
      if (was && !now) finished = true;
      wasUploadingRef.current[key] = now;
    });
    if (finished) {
      fetchEventDetails();
      fetchFolders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadManager.uploads, eventId]);

  // ── Video functions ──
  const fetchVideos = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/events/${eventId}/videos`);
      const data = await res.json();
      setVideos(data.videos || []);
      // Poll any "processing" videos
      (data.videos || []).forEach(v => {
        if (v.status === "processing") pollVideoStatus(v.id);
      });
    } catch {}
  };

  const pollVideoStatus = (videoId) => {
    if (videoPollTimers.current[videoId]) return;
    const poll = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/videos/${videoId}/status`);
        const data = await res.json();
        if (data.status === "ready" || data.status === "failed") {
          delete videoPollTimers.current[videoId];
          fetchVideos();
          return;
        }
        videoPollTimers.current[videoId] = setTimeout(poll, 3000);
      } catch {
        videoPollTimers.current[videoId] = setTimeout(poll, 5000);
      }
    };
    videoPollTimers.current[videoId] = setTimeout(poll, 3000);
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("video/")) { setError("Please select a video file"); return; }
    if (file.size > 2 * 1024 * 1024 * 1024) { setError("Video must be under 2GB"); return; }

    setVideoUploading(true);
    setVideoUploadProgress(0);
    setError("");

    const formData = new FormData();
    formData.append("video", file);
    formData.append("userId", userId);

    try {
      const xhr = new XMLHttpRequest();
      xhr.upload.addEventListener("progress", (evt) => {
        if (evt.lengthComputable) setVideoUploadProgress(Math.round((evt.loaded / evt.total) * 100));
      });
      xhr.addEventListener("load", () => {
        setVideoUploading(false);
        setVideoUploadProgress(0);
        if (xhr.status >= 200 && xhr.status < 300) {
          const data = JSON.parse(xhr.responseText);
          setSuccess("Video uploaded! Transcoding in progress...");
          fetchVideos();
          if (data.videoId) pollVideoStatus(data.videoId);
        } else {
          setError("Video upload failed");
        }
      });
      xhr.addEventListener("error", () => {
        setVideoUploading(false);
        setError("Video upload failed");
      });
      xhr.open("POST", `${API_BASE_URL}/api/events/${eventId}/videos`);
      xhr.send(formData);
    } catch {
      setVideoUploading(false);
      setError("Video upload failed");
    }
    e.target.value = "";
  };

  const handleDeleteVideo = async (videoId) => {
    if (!window.confirm("Delete this video?")) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/videos/${videoId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      // The row used to disappear and the banner used to say "Video deleted"
      // whatever the server answered — including when it refused.
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.message || "Failed to delete video");
        return;
      }
      setVideos(prev => prev.filter(v => v.id !== videoId));
      setSuccess("Video deleted");
    } catch { setError("Failed to delete video"); }
  };

  const copyVideoLink = (shareToken) => {
    const url = `${publicOrigin()}/v/${shareToken}`;
    navigator.clipboard.writeText(url);
    setCopiedVideoLink(shareToken);
    setTimeout(() => setCopiedVideoLink(null), 2000);
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "";
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  // ── Contract function ──
  const fetchContract = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/events/${eventId}/contract`);
      const data = await res.json();
      setContractInfo(data.contract || null);
    } catch {
      setContractInfo(null);
    }
  };

  // ── PDF functions ──
  const fetchPdf = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/events/${eventId}/pdf`);
      if (res.ok) {
        const data = await res.json();
        setPdfInfo(data.pdf);
        // Build review link from token (support both top-level and nested response shapes)
        let token = data?.share_token || data?.pdf?.share_token || "";

        // If no token exists yet, create or fetch one so PDF review link is always usable.
        if (!token && data?.pdf) {
          try {
            const shareRes = await fetch(`${API_BASE_URL}/api/events/${eventId}/share`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
            });
            if (shareRes.ok) {
              const shareData = await shareRes.json();
              token = shareData?.token || "";
            }
          } catch (e) {
            // Keep page usable even if token creation fails.
          }
        }

        if (token) {
          setPdfReviewLink(`${publicOrigin()}/shared/${token}/pdf-review`);
        } else {
          setPdfReviewLink("");
        }
      } else {
        setPdfInfo(null);
        setPdfReviewLink("");
      }
    } catch {
      setPdfInfo(null);
      setPdfReviewLink("");
    }
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== "application/pdf") { setError("Please select a PDF file"); return; }
    // Pro Max has no PDF size cap; every other plan stops at 50MB.
    if (!isProMax && file.size > 50 * 1024 * 1024) { setError("PDF must be under 50MB — upgrade to Pro Max for larger files"); return; }

    setPdfUploading(true);
    setPdfUploadProgress(0);
    setError("");
    try {
      const fd = new FormData();
      fd.append("pdf", file);
      fd.append("userId", userId);

      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", `${API_BASE_URL}/api/events/${eventId}/pdf`);
        xhr.upload.onprogress = (evt) => {
          if (evt.lengthComputable) {
            setPdfUploadProgress(Math.round((evt.loaded / evt.total) * 100));
          }
        };
        xhr.onload = () => {
          try {
            const data = JSON.parse(xhr.responseText);
            if (xhr.status >= 200 && xhr.status < 300) {
              setSuccess("PDF uploaded!");
              fetchPdf();
              resolve();
            } else {
              setError(data.message || "PDF upload failed");
              reject(new Error(data.message));
            }
          } catch {
            setError("PDF upload failed");
            reject(new Error("Parse error"));
          }
        };
        xhr.onerror = () => { setError("PDF upload failed"); reject(new Error("Network error")); };
        xhr.send(fd);
      });
    } catch { /* errors already set above */ }
    finally { setPdfUploading(false); setPdfUploadProgress(0); }
    e.target.value = "";
  };

  const handleDeletePdf = async () => {
    if (!window.confirm("Delete this PDF and all reviews?")) return;
    try {
      await fetch(`${API_BASE_URL}/api/events/${eventId}/pdf`, { method: "DELETE" });
      setPdfInfo(null);
      setPdfSubmissions([]);
      setPdfReviewLink("");
      setSuccess("PDF deleted");
    } catch { setError("Failed to delete PDF"); }
  };

  const fetchPdfSubmissions = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/events/${eventId}/pdf/submissions`);
      if (res.ok) {
        const data = await res.json();
        const subs = data.submissions || [];
        setPdfSubmissions(subs);
        // Also update the pdfInfo count
        if (pdfInfo) {
          setPdfInfo({ ...pdfInfo, pdf_review_count: subs.length });
        }
      }
    } catch {}
  };

  const toggleSubmissionLock = async (sub) => {
    const newLocked = !sub.review_locked;
    try {
      const res = await fetch(`${API_BASE_URL}/api/events/${eventId}/pdf/submissions/${sub.id}/lock`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ locked: newLocked }),
      });
      if (res.ok) {
        setPdfSubmissions((prev) =>
          prev.map((s) => (s.id === sub.id ? { ...s, review_locked: newLocked } : s))
        );
      }
    } catch {}
  };

  const copyPdfReviewLink = () => {
    if (pdfReviewLink) {
      navigator.clipboard.writeText(pdfReviewLink);
      setSuccess("PDF review link copied!");
      setTimeout(() => setSuccess(""), 2000);
    }
  };

  const handleGenerateShareLink = async () => {
    setShareLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/events/${eventId}/share`,
        { method: "POST", headers: { "Content-Type": "application/json" } }
      );
      const data = await response.json();
      if (response.ok) {
        const fullUrl = `${publicOrigin()}/shared/${data.token}`;
        setShareLink(fullUrl);
        setShowShareModal(true);
      } else {
        setError(data.message || "Failed to generate share link");
      }
    } catch (err) {
      setError("Failed to generate share link");
    } finally {
      setShareLoading(false);
    }
  };

  const handleOpenLiveDisplay = async () => {
    setShareLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/events/${eventId}/share`,
        { method: "POST", headers: { "Content-Type": "application/json" } }
      );
      const data = await response.json();
      if (response.ok) {
        window.open(`${publicOrigin()}/shared/${data.token}/slideshow`, "_blank");
      } else {
        setError(data.message || "Failed to open live display");
      }
    } catch (err) {
      setError("Failed to open live display");
    } finally {
      setShareLoading(false);
    }
  };

  const saveGalleryTheme = async (theme) => {
    if (theme === galleryTheme || savingTheme) return;
    const previous = galleryTheme;
    setGalleryTheme(theme); // optimistic: the picker should feel instant
    setSavingTheme(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/events/${eventId}/gallery-theme`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ theme }),
      });
      if (!res.ok) throw new Error("save failed");
      const data = await res.json();
      setGalleryTheme(data.galleryTheme);
      setEvent((prev) => ({ ...prev, gallery_theme: data.galleryTheme }));
      setSuccess(theme === "grid" ? "Galleries now use the Grid theme" : "Galleries now use the Magazine theme");
      setTimeout(() => setSuccess(""), 2500);
    } catch (err) {
      setGalleryTheme(previous);
      setError("Could not change the theme — please try again");
      setTimeout(() => setError(""), 2500);
    } finally {
      setSavingTheme(false);
    }
  };

  // The client's selection, presented as the same magazine spread the person
  // galleries use. The share token is idempotent server-side, so this works
  // whether or not the studio ever opened the Share modal on this event.
  const openSelectionGallery = async () => {
    setShareLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/events/${eventId}/share`,
        { method: "POST", headers: { "Content-Type": "application/json" } }
      );
      const data = await response.json();
      if (response.ok) {
        window.open(`${publicOrigin()}/shared/${data.token}/selection`, "_blank", "noopener,noreferrer");
      } else {
        setError(data.message || "Failed to open the gallery");
      }
    } catch (err) {
      setError("Failed to open the gallery");
    } finally {
      setShareLoading(false);
    }
  };

  const copyShareLink = () => {
    navigator.clipboard.writeText(shareLink);
    setSuccess("Link copied to clipboard!");
    setTimeout(() => setSuccess(""), 2000);
  };

  const downloadQrCode = (svgId, suffix) => {
    const size = 1024;
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    const svgEl = document.getElementById(svgId);
    if (!svgEl) return;
    const svgData = new XMLSerializer().serializeToString(svgEl);
    const img = new Image();
    img.onload = () => {
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, size, size);
      ctx.drawImage(img, 0, 0, size, size);
      const a = document.createElement("a");
      a.download = `${event?.name || "event"}-${suffix || "qr"}.png`;
      a.href = canvas.toDataURL("image/png");
      a.click();
    };
    img.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svgData);
  };

  const handleLiveShare = async () => {
    if (qrGalleryIdRef.current && liveShareLink) {
      setShowLiveShareModal(true);
      syncImagesToQrGallery(qrGalleryIdRef.current);
      return;
    }
    setLiveShareLoading(true);
    try {
      const galleryName = event?.name || `Event ${eventId}`;
      const res = await fetch(`${API_BASE_URL}/api/qr-galleries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId,
          name: galleryName,
          eventType: "Wedding",
          place: "",
          eventDate: event?.event_date || "",
        }),
      });
      const data = await res.json();
      if (res.ok) {
        const gallery = data.gallery;
        qrGalleryIdRef.current = gallery.id;
        const fullUrl = `${publicOrigin()}/qr/${gallery.token}`;
        setLiveShareLink(fullUrl);
        setShowLiveShareModal(true);
        syncImagesToQrGallery(gallery.id);
      } else {
        setError(data.message || "Failed to create QR sharing gallery");
      }
    } catch {
      setError("Failed to create QR sharing gallery");
    } finally {
      setLiveShareLoading(false);
    }
  };

  const syncImagesToQrGallery = async (galleryId) => {
    try {
      await fetch(`${API_BASE_URL}/api/qr-galleries/${galleryId}/sync-from-event`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ eventId: parseInt(eventId) }),
      });
    } catch { /* background sync, ignore errors */ }
  };

  const copyLiveShareLink = () => {
    navigator.clipboard.writeText(liveShareLink);
    setSuccess("QR sharing link copied!");
    setTimeout(() => setSuccess(""), 2000);
  };

  const fetchVotedImages = async (filter) => {
    try {
      const url = filter === "all"
        ? `${API_BASE_URL}/api/events/${eventId}/votes`
        : `${API_BASE_URL}/api/events/${eventId}/votes?filter=${filter}`;
      const response = await fetch(url);
      const data = await response.json();
      if (response.ok) {
        setVotedImages(data.images);
      }
    } catch (err) {
      console.error("Failed to fetch votes:", err);
    }
  };

  const handleVoteFilterChange = (filter) => {
    setVoteFilter(filter);
    setPhotoIndex(0);
    if (filter !== "all") {
      fetchVotedImages(filter);
    }
  };

  // Fetch images for the selected / not-selected folders
  const fetchFolderImages = async (folder) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/events/${eventId}/votes?filter=${folder}`
      );
      const data = await response.json();
      if (response.ok) {
        if (folder === "selected") setSelectedImages(data.images);
        else setUnselectedImages(data.images);
      }
    } catch (err) {
      console.error(`Failed to fetch ${folder} images:`, err);
    }
  };

  const toggleFolder = (folder) => {
    if (openFolder === folder) {
      setOpenFolder(null);
    } else {
      setOpenFolder(folder);
      fetchFolderImages(folder);
    }
  };

  const displayedPhotos = voteFilter !== "all" ? votedImages : images;

  const goToPhoto = (dir) => {
    setPhotoIndex((prev) => {
      if (dir === "prev") return prev <= 0 ? displayedPhotos.length - 1 : prev - 1;
      return prev >= displayedPhotos.length - 1 ? 0 : prev + 1;
    });
  };

  const saveSelectionLimit = async (overrideInput) => {
    setSavingLimit(true);
    try {
      const raw = overrideInput !== undefined ? overrideInput : selectionLimitInput;
      const limit = raw.trim() === "" ? null : parseInt(raw, 10);
      const res = await fetch(`${API_BASE_URL}/api/events/${eventId}/selection-limit`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
        body: JSON.stringify({ selectionLimit: limit }),
      });
      const data = await res.json();
      if (res.ok) {
        setEvent(prev => ({ ...prev, selection_limit: data.selectionLimit }));
        setSuccess(data.selectionLimit == null ? "Selection limit removed" : `Limit set to ${data.selectionLimit} photos`);
        setTimeout(() => setSuccess(""), 2500);
      } else {
        setError(data.message || "Failed to save limit");
      }
    } catch {
      setError("Failed to save limit");
    } finally {
      setSavingLimit(false);
    }
  };

  const downloadImage = async (imageId, filename) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/images/${imageId}/download`);
      if (!response.ok) throw new Error("Download failed");
      const blob = await response.blob();
      // Extract filename from Content-Disposition header if available
      const disposition = response.headers.get("Content-Disposition");
      let downloadName = filename;
      if (disposition) {
        const match = disposition.match(/filename="?([^"]+)"?/);
        if (match) downloadName = match[1];
      }
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = downloadName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (err) {
      setError("Failed to download image");
      setTimeout(() => setError(""), 2000);
    }
  };

  // Which upload folder an image belongs to, for the download-filter checkboxes.
  const uploadFolderKeyOf = (img) => (img.folder_id == null ? "general" : String(img.folder_id));
  const uploadFolderLabelOf = (img) => img.folder_name || event?.general_folder_name || "General";

  const toggleDownloadFolder = (section, key) => {
    setDownloadExcludedFolders((prev) => {
      const next = new Set(prev[section]);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return { ...prev, [section]: next };
    });
  };

  // variant "jpeg" zips the gallery JPEGs; "raw" zips the original camera files
  // (Pro Max only, and only for photos that actually have a RAW backup).
  const downloadAllInFolder = async (folder, variant = "jpeg") => {
    const allImgs = folder === "selected" ? selectedImages : unselectedImages;
    const excluded = downloadExcludedFolders[folder];
    let imgs = allImgs.filter((img) => !excluded.has(uploadFolderKeyOf(img)));
    if (variant === "raw") imgs = imgs.filter((img) => img.has_raw);
    if (imgs.length === 0) return;
    const busyKey = variant === "raw" ? `${folder}-raw` : folder;
    setDownloadingFolder(busyKey);
    setDownloadProgress({ folder: busyKey, received: 0, total: 0, pct: null });
    const suffix = variant === "raw" ? `${folder}_RAW` : folder;
    const zipName = event ? `${event.groom_name || ""} and ${event.bridal_name || ""}_${suffix}`.trim() : `${suffix}_photos`;
    try {
      const imageIds = imgs.map(img => img.id);

      // Read the archive as it streams so the bar reflects real bytes. On
      // desktop this writes straight to disk instead of accumulating chunks in
      // memory — a RAW archive for a full wedding does not fit in a renderer.
      const result = await downloadFromApi({
        url: `${API_BASE_URL}/api/events/${eventId}/download-zip`,
        filename: `${zipName}.zip`,
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
        body: JSON.stringify({ imageIds, folderName: zipName, variant }),
        onProgress: ({ received, total, pct }) =>
          setDownloadProgress({ folder: busyKey, received, total, pct }),
      });

      if (result?.cancelled) {
        setDownloadProgress(null);
      } else {
        setSuccess(`Downloaded ${imgs.length} ${folder} ${variant === "raw" ? "RAW files" : "photos"} as ZIP!`);
      }
    } catch (err) {
      setError(`Failed to download ${variant === "raw" ? "RAW files" : "photos"}`);
    }
    setDownloadingFolder(null);
    setDownloadProgress(null);
    setTimeout(() => { setSuccess(""); setError(""); }, 2000);
  };

  const handleFolderCreated = (folder) => setFolders((prev) => [...prev, folder]);
  const handleFolderDeleted = (folderId, deletedImages) => {
    setFolders((prev) => prev.filter((f) => f.id !== folderId));
    const wasActiveFilter = String(folderFilter) === String(folderId);
    // If the deleted folder was the active filter, switch back to "all" — the
    // gallery-refetch effect below picks that filter change up on its own.
    if (wasActiveFilter) setFolderFilter("all");

    if (deletedImages > 0) {
      // Its photos were permanently deleted server-side — refresh the event
      // total always, and re-pull the gallery too when it wasn't already
      // about to refetch from the filter change above (i.e. viewing "all",
      // which spans every folder including the one just removed).
      fetchEventDetails();
      if (!wasActiveFilter && folderFilter === "all") {
        setImages([]);
        seenImageIdsRef.current = new Set();
        fetchImagesPage(0, "all");
      }
    }
  };
  const handleFolderRenamed = (folderId, newName) => {
    setFolders((prev) => prev.map((f) => (f.id === folderId ? { ...f, name: newName } : f)));
  };
  const handleGeneralFolderRenamed = (newName) => {
    setEvent((prev) => ({ ...prev, general_folder_name: newName }));
  };

  const handleDeleteImage = async (imageId) => {
    if (window.confirm("Delete this photo?")) {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/events/images/${imageId}`,
          { method: "DELETE", headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
        );

        if (!response.ok) throw new Error("Failed to delete");

        setImages(images.filter((img) => img.id !== imageId));
        seenImageIdsRef.current.delete(imageId);
        setTotalImages((prev) => Math.max(0, prev - 1));
        setPhotoIndex((prev) => prev > 0 ? prev - 1 : 0);
        setSuccess("Photo deleted successfully!");
        setTimeout(() => setSuccess(""), 2000);
      } catch (err) {
        setError("Failed to delete photo");
      }
    }
  };

  const openLightbox = (index, source) => { setLightboxIndex(index); setLightboxSource(source); };
  const closeLightbox = () => { setLightboxIndex(-1); setLightboxSource(null); };
  const getLightboxImages = () => {
    if (lightboxSource === 'grid') return displayedPhotos;
    if (lightboxSource === 'selected') return selectedImages;
    if (lightboxSource === 'unselected') return unselectedImages;
    return [];
  };

  useEffect(() => {
    if (lightboxIndex < 0) return;
    const handler = (e) => {
      const imgs = getLightboxImages();
      if (e.key === 'Escape') closeLightbox();
      else if (e.key === 'ArrowRight' && lightboxIndex < imgs.length - 1) setLightboxIndex(lightboxIndex + 1);
      else if (e.key === 'ArrowLeft' && lightboxIndex > 0) setLightboxIndex(lightboxIndex - 1);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  const handleLogout = () => {
    clearAuthStorage();
    navigate("/login");
  };

  if (loading) {
    return (
      <div className="ed-container">
        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <div className="ed-main-content">
          <header className="ed-header">
            <div className="ed-header-left">
              <button
                className="ed-toggle-sidebar"
                onClick={() => setSidebarOpen(!sidebarOpen)}
              >
                ☰
              </button>
            </div>
          </header>
          <CameraLoader text="Loading event..." />
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="ed-container">
        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <div className="ed-main-content">
          <header className="ed-header">
            <div className="ed-header-left">
              <button
                className="ed-toggle-sidebar"
                onClick={() => setSidebarOpen(!sidebarOpen)}
              >
                ☰
              </button>
              <h1 className="ed-page-title">Event Not Found</h1>
            </div>
            <div className="ed-header-right">
              <button className="ed-logout-btn" onClick={handleLogout}>
                Logout
              </button>
            </div>
          </header>
        </div>
      </div>
    );
  }

  return (
    <div className="ed-container">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="ed-main-content">
        <header className="ed-header">
          <div className="ed-header-left">
            <button
              className="ed-toggle-sidebar"
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              ☰
            </button>
            <h1 className="ed-page-title">{event.event_name}</h1>
          </div>
          <div className="ed-header-right">
            <button
              className="ed-share-btn"
              onClick={() => navigate(`/events/${eventId}/invoice`)}
            >
              🧾 Invoice
            </button>
            <button
              className="ed-share-btn"
              onClick={() => navigate(`/events/${eventId}/contract`)}
            >
              ✍️ Contract
            </button>
            <button
              className="ed-share-btn"
              onClick={handleGenerateShareLink}
              disabled={shareLoading}
            >
              {shareLoading ? "Generating..." : "🔗 Selection Link"}
            </button>
            <button
              className="ed-share-btn ed-qr-share-btn"
              onClick={handleLiveShare}
              disabled={liveShareLoading}
            >
              {liveShareLoading ? "Generating..." : "📱 QR Sharing"}
            </button>
            <button
              className="ed-share-btn"
              onClick={handleOpenLiveDisplay}
              disabled={shareLoading}
            >
              🖥️ Live Display
            </button>
            <button
              className="ed-back-btn"
              onClick={() => navigate("/events")}
            >
              ← Back
            </button>
          </div>
        </header>

        <main className="ed-main">
          {/* Top Row: Info + Upload */}
          <div className="ed-details-card ed-details-card-full">
            <div className="ed-details-inline">
              <div className="ed-detail-chip">
                <span className="ed-chip-label">👰 Bride</span>
                <span className="ed-chip-value">{event.bridal_name}</span>
              </div>
              <div className="ed-detail-chip">
                <span className="ed-chip-label">🤵 Groom</span>
                <span className="ed-chip-value">{event.groom_name}</span>
              </div>
              <div className="ed-detail-chip">
                <span className="ed-chip-label">📅 Date</span>
                <span className="ed-chip-value">
                  {event.event_date
                    ? new Date(event.event_date).toLocaleDateString()
                    : "—"}
                </span>
              </div>
              <div className="ed-detail-chip">
                <span className="ed-chip-label">📸 Photos</span>
                <span className="ed-chip-value">{event.image_count || 0}</span>
              </div>
            </div>
          </div>

          {/* Upload Folders — e.g. "Candid", "Traditional". Each has its own
              dropzone and progress, but they all share the same event-wide
              photo quota (never counted per folder). */}
          <div className="ed-folders-section">
            <h2 className="ed-section-title">☁️ Upload Photos</h2>
            <FolderManager
              eventId={eventId}
              eventName={event.event_name}
              folders={folders}
              onFolderCreated={handleFolderCreated}
              onFolderDeleted={handleFolderDeleted}
              onFolderRenamed={handleFolderRenamed}
              generalCount={Math.max(0, (event.image_count || 0) - folders.reduce((s, f) => s + (f.image_count || 0), 0))}
              generalFolderName={event.general_folder_name}
              onGeneralFolderRenamed={handleGeneralFolderRenamed}
              isProMax={isProMax}
            />
          </div>

          {error && <div className="ed-error-message">{error}</div>}
          {success && <div className="ed-success-message">{success}</div>}

          {isProMax && jpegOnlyRaws.length > 0 && (
            <div className="ed-raw-info-banner">
              <span>
                {jpegOnlyRaws.length} photo{jpegOnlyRaws.length > 1 ? "s were" : " was"} uploaded as JPEG only — the RAW original{jpegOnlyRaws.length > 1 ? "s are" : " is"} still on the camera card.
                Drag those files in again to add {jpegOnlyRaws.length > 1 ? "them" : "it"}.
              </span>
            </div>
          )}

          {isProMax && missingRawBackups.length > 0 && (
            <div className="ed-raw-missing-banner">
              <div className="ed-raw-missing-text">
                <strong>⚠️ {missingRawBackups.length} RAW original{missingRawBackups.length > 1 ? "s" : ""} didn't finish backing up.</strong>
                <span>
                  The gallery photos are safe — only the camera originals are missing
                  {missingRawBackups.length <= 5 && ` (${missingRawBackups.map((m) => m.original_filename).join(", ")})`}.
                  Re-select those files to finish the backup.
                </span>
              </div>
              <button
                className="ed-raw-missing-btn"
                onClick={() => restoreRawInputRef.current?.click()}
                disabled={restoringRaw}
              >
                {restoringRaw ? "⏳ Restoring..." : "Restore RAW backups"}
              </button>
              <input
                ref={restoreRawInputRef}
                type="file"
                multiple
                accept=".cr2,.cr3,.nef,.arw,.dng,.orf,.raf,.rw2,.pef,.srw,.x3f,.3fr,.mrw,.nrw,.rwl"
                style={{ display: "none" }}
                onChange={handleRestoreRawBackups}
              />
            </div>
          )}

          {/* Client Selections Folders */}
          <div className="ed-folders-section">
            <h2 className="ed-section-title">📂 Client Selections</h2>

            {/* Which layout the client's galleries render in. Applies to the
                person galleries and the Client AI Gallery on this event's
                share link — the studio can open either to see the result. */}
            <div className="ed-theme-card">
              <div className="ed-selection-limit-info">
                <span className="ed-selection-limit-label">🎨 Gallery theme</span>
                <span className="ed-selection-limit-sub">
                  How this event's shared galleries look to your client
                </span>
              </div>
              <div className="ed-theme-options">
                {GALLERY_THEMES.map((t) => (
                  <button
                    key={t.key}
                    className={`ed-theme-option${galleryTheme === t.key ? ' active' : ''}`}
                    onClick={() => saveGalleryTheme(t.key)}
                    disabled={savingTheme}
                    aria-pressed={galleryTheme === t.key}
                  >
                    {/* The palette itself is the preview — paper, accent and
                        ink, in the arrangement the theme uses. */}
                    <span
                      className={`ed-theme-preview ed-theme-preview--${t.layout}`}
                      style={{ background: t.swatch[0], borderColor: t.swatch[2] + '22' }}
                      aria-hidden="true"
                    >
                      <i style={{ background: t.swatch[2] }} />
                      <i style={{ background: t.swatch[1] }} />
                      <i style={{ background: t.swatch[2], opacity: 0.55 }} />
                      <i style={{ background: t.swatch[1], opacity: 0.4 }} />
                    </span>
                    <span className="ed-theme-name">
                      <span className="ed-theme-mark" style={{ color: t.swatch[1] }}>{t.mark}</span>
                      {t.name}
                    </span>
                    <span className="ed-theme-desc">{t.desc}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Selection limit setting */}
            <div className="ed-selection-limit-card">
              <div className="ed-selection-limit-info">
                <span className="ed-selection-limit-label">Photo selection limit</span>
                <span className="ed-selection-limit-sub">
                  {event?.selection_limit != null
                    ? `Clients can select up to ${event.selection_limit} photos`
                    : "Unlimited — clients can select any number of photos"}
                </span>
              </div>
              <div className="ed-selection-limit-controls">
                <input
                  type="number"
                  min="1"
                  placeholder="No limit"
                  value={selectionLimitInput}
                  onChange={e => setSelectionLimitInput(e.target.value)}
                  className="ed-selection-limit-input"
                />
                <button
                  className="ed-selection-limit-btn"
                  onClick={() => saveSelectionLimit()}
                  disabled={savingLimit}
                >
                  {savingLimit ? "Saving…" : "Save"}
                </button>
                {event?.selection_limit != null && (
                  <button
                    className="ed-selection-limit-clear"
                    onClick={() => { setSelectionLimitInput(""); saveSelectionLimit(""); }}
                    disabled={savingLimit}
                  >
                    Remove limit
                  </button>
                )}
              </div>
            </div>

            <div className="ed-folders-grid">
              {["selected", "unselected"].map((folder) => {
                const imgs = folder === "selected" ? selectedImages : unselectedImages;
                const isOpen = openFolder === folder;
                const excluded = downloadExcludedFolders[folder];
                const availableUploadFolders = [];
                const seenKeys = new Set();
                imgs.forEach((img) => {
                  const key = uploadFolderKeyOf(img);
                  if (!seenKeys.has(key)) {
                    seenKeys.add(key);
                    availableUploadFolders.push({ key, name: uploadFolderLabelOf(img) });
                  }
                });
                const downloadCount = imgs.filter((img) => !excluded.has(uploadFolderKeyOf(img))).length;
                const rawCount = imgs.filter((img) => !excluded.has(uploadFolderKeyOf(img)) && img.has_raw).length;
                return (
                  <div key={folder} className={`ed-folder ${isOpen ? "open" : ""}`}>
                    <div className="ed-folder-header" onClick={() => toggleFolder(folder)}>
                      <div className="ed-folder-info">
                        <span className={`ed-folder-icon ${folder}`}>
                          {folder === "selected" ? "✅" : "❌"}
                        </span>
                        <div>
                          <h3 className="ed-folder-name">{folder === "selected" ? "Selected Photos" : "Not Selected Photos"}</h3>
                          <p className="ed-folder-count">
                            {isOpen ? `${imgs.length} photos` : "Click to view"}
                          </p>
                        </div>
                      </div>
                      <span className="ed-folder-arrow">{isOpen ? "▼" : "▶"}</span>
                    </div>
                    {isOpen && (
                      <div className="ed-folder-content">
                        {availableUploadFolders.length > 1 && (
                          <div className="ed-download-folder-checks">
                            <span className="ed-download-folder-checks-label">Include in download:</span>
                            {availableUploadFolders.map((f) => (
                              <label key={f.key} className="ed-download-folder-check">
                                <input
                                  type="checkbox"
                                  checked={!excluded.has(f.key)}
                                  onChange={() => toggleDownloadFolder(folder, f.key)}
                                />
                                📁 {f.name}
                              </label>
                            ))}
                          </div>
                        )}
                        {imgs.length > 0 && (
                          <div className="ed-download-zip-row">
                            {folder === "selected" && (
                              <button
                                className="ed-download-all-btn ed-ai-gallery-btn"
                                onClick={openSelectionGallery}
                                disabled={shareLoading}
                                title="Open the client's selection as a magazine-style gallery"
                              >
                                {shareLoading ? "⏳ Opening..." : `✨ Client AI Gallery (${imgs.length})`}
                              </button>
                            )}
                            <button
                              className={`ed-download-all-btn ${folder === "unselected" ? "unselected" : ""}`}
                              onClick={() => downloadAllInFolder(folder)}
                              disabled={downloadingFolder != null || downloadCount === 0}
                            >
                              {downloadingFolder === folder ? "⏳ Downloading..." : `⬇️ Download ZIP — JPEG (${downloadCount})`}
                            </button>
                            {isProMax && rawCount > 0 && (
                              <button
                                className={`ed-download-all-btn ed-download-all-raw ${folder === "unselected" ? "unselected" : ""}`}
                                onClick={() => downloadAllInFolder(folder, "raw")}
                                disabled={downloadingFolder != null}
                              >
                                {downloadingFolder === `${folder}-raw`
                                  ? "⏳ Downloading..."
                                  : `⬇️ Download ZIP — RAW (${rawCount})`}
                              </button>
                            )}
                          </div>
                        )}

                        {downloadProgress && downloadProgress.folder && downloadProgress.folder.startsWith(folder) && (
                          <div className="ed-dl-progress">
                            <div className="ed-dl-progress-bar">
                              <div
                                className={`ed-dl-progress-fill ${downloadProgress.pct == null ? "indeterminate" : ""}`}
                                style={downloadProgress.pct == null ? undefined : { width: `${downloadProgress.pct}%` }}
                              />
                            </div>
                            <span className="ed-dl-progress-text">
                              {downloadProgress.pct == null
                                ? `Preparing — ${(downloadProgress.received / 1048576).toFixed(1)} MB received`
                                : `${downloadProgress.pct}% — ${(downloadProgress.received / 1048576).toFixed(1)} MB${
                                    downloadProgress.total ? ` of ~${(downloadProgress.total / 1048576).toFixed(0)} MB` : ""
                                  }`}
                            </span>
                          </div>
                        )}
                        {imgs.length === 0 ? (
                          <p className="ed-folder-empty">No {folder} photos yet</p>
                        ) : (
                          <div className="ed-folder-gallery">
                            {imgs.map((image, index) => (
                              <div key={image.id} className="ed-folder-card">
                                <img src={image.thumbnail_url || image.image_url} alt={`${folder} ${index + 1}`} className="ed-folder-photo" onClick={() => openLightbox(index, folder)} style={{ cursor: 'pointer' }} />
                                {image.has_raw && <span className="ed-folder-raw-tag">RAW</span>}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Photos Carousel */}
          <div className="ed-gallery-section">
            <div className="ed-gallery-header">
              <h2 className="ed-section-title">
                🖼️ Photos ({totalImages})
                {folderFilter !== "all" && (
                  <span className="ed-gallery-folder-label">
                    {" "}— {folderFilter === "general" ? (event.general_folder_name || "General") : folders.find((f) => String(f.id) === String(folderFilter))?.name}
                  </span>
                )}
              </h2>
              <div className="ed-vote-filter">
                {["all", "selected", "unselected"].map((f) => (
                  <button
                    key={f}
                    className={`ed-filter-btn ${voteFilter === f ? "active" : ""}`}
                    onClick={() => handleVoteFilterChange(f)}
                  >
                    {f === "all" ? "All" : f === "selected" ? "✅ Selected" : "⬜ Not selected"}
                  </button>
                ))}
              </div>
            </div>

            {folders.length > 0 && (
              <div className="ed-vote-filter ed-folder-filter">
                {[{ id: "all", name: "All folders" }, { id: "general", name: event.general_folder_name || "General" }, ...folders].map((f) => (
                  <button
                    key={f.id}
                    className={`ed-filter-btn ${String(folderFilter) === String(f.id) ? "active" : ""}`}
                    onClick={() => setFolderFilter(f.id)}
                  >
                    📁 {f.name}
                  </button>
                ))}
              </div>
            )}

            {displayedPhotos.length === 0 ? (
              <div className="ed-no-photos">
                <p className="ed-no-photos-icon">📷</p>
                <p className="ed-no-photos-text">
                  {voteFilter !== "all"
                    ? `No ${voteFilter} photos found.`
                    : "No photos yet. Upload your first photo!"}
                </p>
              </div>
            ) : (
              <div className="ed-thumb-grid" ref={thumbGridRef}>
                {displayedPhotos.map((photo, i) => (
                  <div key={photo.id} className="ed-thumb-item" onClick={() => openLightbox(i, 'grid')}>
                    <img
                      src={photo.thumbnail_url || photo.image_url}
                      alt={`Photo ${i + 1}`}
                      className="ed-thumb-img"
                      loading="lazy"
                      decoding="async"
                    />
                    <div className="ed-thumb-overlay">
                      <button
                        className="ed-thumb-del"
                        onClick={(e) => { e.stopPropagation(); handleDeleteImage(photo.id); }}
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </div>
                    {photo.votes && photo.votes !== '[]' && (
                      <span className="ed-thumb-vote">
                        {(typeof photo.votes === 'string' ? JSON.parse(photo.votes) : photo.votes)
                          .some(v => v.vote === 'selected') ? '✅' : '⬜'}
                      </span>
                    )}
                  </div>
                ))}
                {voteFilter === "all" && hasMoreImages && (
                  <div ref={loadMoreSentinelRef} className="ed-thumb-load-more">
                    {loadingMoreImages ? (
                      "Loading more…"
                    ) : (
                      <button className="ed-load-more-btn" onClick={loadMoreImages}>
                        Load more photos ({totalImages - images.length} remaining)
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── Video Section ── */}
          <div className="ed-video-section">
            <h2 className="ed-section-title">🎬 Videos</h2>
            <div className="ed-video-upload-area">
              <input
                type="file"
                accept="video/*"
                onChange={handleVideoUpload}
                className="ed-file-input"
                disabled={videoUploading}
              />
              <div className="ed-upload-content">
                <span className="ed-upload-icon">🎥</span>
                <p className="ed-upload-main">
                  {videoUploading ? `Uploading video... ${videoUploadProgress}%` : "Click to upload a video (max 2GB)"}
                </p>
              </div>
            </div>

            {videoUploading && (
              <div className="ed-progress-container">
                <div className="ed-progress-bar">
                  <div className="ed-progress-fill" style={{ width: `${videoUploadProgress}%` }} />
                </div>
                <span className="ed-progress-text">Uploading: {videoUploadProgress}%</span>
              </div>
            )}

            {videos.length > 0 && (
              <div className="ed-videos-grid">
                {videos.map((v) => (
                  <div key={v.id} className="ed-video-card">
                    <div className="ed-video-thumb" onClick={() => v.status === "ready" && navigate(`/v/${v.share_token}`)}>
                      {v.thumbnail_url ? (
                        <img src={v.thumbnail_url} alt={v.original_filename} />
                      ) : (
                        <div className="ed-video-thumb-placeholder">
                          {v.status === "processing" ? "⏳" : v.status === "failed" ? "❌" : "🎬"}
                        </div>
                      )}
                      {v.status === "ready" && v.duration && (
                        <span className="ed-video-duration">{formatDuration(v.duration)}</span>
                      )}
                      {v.status === "processing" && <div className="ed-video-processing">Processing...</div>}
                      {v.status === "failed" && <div className="ed-video-failed">Failed</div>}
                    </div>
                    <div className="ed-video-info">
                      <span className="ed-video-name" title={v.original_filename}>
                        {v.original_filename?.length > 25 ? v.original_filename.slice(0, 22) + "..." : v.original_filename}
                      </span>
                      <div className="ed-video-actions">
                        {v.status === "ready" && v.share_token && (
                          <button
                            className="ed-video-share-btn"
                            onClick={() => copyVideoLink(v.share_token)}
                            title="Copy share link"
                          >
                            {copiedVideoLink === v.share_token ? "✅ Copied" : "🔗 Share"}
                          </button>
                        )}
                        <button className="ed-video-delete-btn" onClick={() => handleDeleteVideo(v.id)} title="Delete">
                          🗑
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── PDF Review Section ── */}
          <div className="ed-video-section">
            <h2 className="ed-section-title">📄 PDF Review</h2>

            {!pdfInfo ? (
              <div className="ed-video-upload-area">
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={handlePdfUpload}
                  className="ed-file-input"
                  disabled={pdfUploading}
                />
                {pdfUploading ? (
                  <div className="ed-pdf-upload-progress">
                    <span className="ed-upload-icon">📄</span>
                    <p className="ed-upload-main">Uploading PDF... {pdfUploadProgress}%</p>
                    <div className="ed-progress-bar" style={{ marginTop: '12px' }}>
                      <div className="ed-progress-fill" style={{ width: `${pdfUploadProgress}%` }} />
                    </div>
                  </div>
                ) : (
                  <div className="ed-upload-content">
                    <span className="ed-upload-icon">📄</span>
                    <p className="ed-upload-main">Upload a PDF for client review {isProMax ? "(no size limit)" : "(max 50MB)"}</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="ed-pdf-info-card">
                <div className="ed-pdf-info-row">
                  <span className="ed-pdf-filename">📄 {pdfInfo.original_filename}</span>
                  <span className="ed-pdf-pages">{pdfInfo.page_count || "?"} pages</span>
                </div>
                <div className="ed-pdf-actions">
                  {pdfReviewLink && (
                    <button className="ed-pdf-copy-btn" onClick={copyPdfReviewLink}>
                      🔗 Copy Review Link
                    </button>
                  )}
                  <button className="ed-pdf-reviews-btn" onClick={() => { fetchPdfSubmissions(); setShowPdfSubmissions(true); }}>
                    📋 View Reviews ({pdfSubmissions.length > 0 ? pdfSubmissions.length : (pdfInfo?.pdf_review_count || 0)})
                  </button>
                  <button className="ed-pdf-delete-btn" onClick={handleDeletePdf}>🗑</button>
                </div>
              </div>
            )}
          </div>

          {/* ── Contract Section ── */}
          <div className="ed-video-section">
            <h2 className="ed-section-title">✍️ Contract</h2>

            {!contractInfo ? (
              <div className="ed-video-upload-area" onClick={() => navigate(`/events/${eventId}/contract`)} style={{ cursor: "pointer" }}>
                <div className="ed-upload-content">
                  <span className="ed-upload-icon">✍️</span>
                  <p className="ed-upload-main">No contract yet — click to upload one</p>
                </div>
              </div>
            ) : (
              <div className="ed-pdf-info-card">
                <div className="ed-pdf-info-row">
                  <span className="ed-pdf-filename">✍️ {contractInfo.original_filename}</span>
                  <span className={`ed-pdf-pages ${contractInfo.status === "signed" ? "ed-contract-signed-badge" : ""}`}>
                    {contractInfo.status === "signed" ? "✓ Signed" : "Awaiting signature"}
                  </span>
                </div>
                {contractInfo.status === "signed" && contractInfo.signer_name && (
                  <p className="ed-contract-signed-meta">
                    Signed by {contractInfo.signer_name}
                    {contractInfo.signed_at ? ` on ${new Date(contractInfo.signed_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}` : ""}
                  </p>
                )}
                <div className="ed-pdf-actions">
                  {contractInfo.status === "signed" ? (
                    <a
                      className="ed-pdf-reviews-btn"
                      href={`${API_BASE_URL}/api/events/${eventId}/contract/signed-file`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      ⬇ Download Signed Contract
                    </a>
                  ) : (
                    <button className="ed-pdf-reviews-btn" onClick={() => navigate(`/events/${eventId}/contract`)}>
                      📤 Send / Manage
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ── People / Face Clustering Section ── */}
          <div className="ed-video-section">
            <h2 className="ed-section-title">👥 People</h2>
            <PeopleTab eventId={eventId} />
          </div>

          {/* ── Analytics Section ── */}
          <div className="ed-video-section">
            <h2 className="ed-section-title">📊 Analytics</h2>
            <ErrorBoundary variant="inline" label="EventAnalytics">
              <EventAnalytics eventId={eventId} />
            </ErrorBoundary>
          </div>

          {/* PDF Submissions Modal */}
          {showPdfSubmissions && (
            <div className="ed-modal-overlay" onClick={() => setShowPdfSubmissions(false)}>
              <div className="ed-modal ed-pdf-submissions-modal" onClick={(e) => e.stopPropagation()}>
                <h3>📋 PDF Review Submissions</h3>
                {pdfSubmissions.length === 0 ? (
                  <p style={{ color: "#6b7280", textAlign: "center", padding: "20px 0" }}>No reviews submitted yet.</p>
                ) : (
                  <div className="ed-pdf-submissions-list">
                    {pdfSubmissions.map((sub) => (
                      <div key={sub.id} className="ed-pdf-submission-card">
                        <div className="ed-pdf-sub-header">
                          <div>
                            <strong>{sub.client_name}</strong>
                            <span className="ed-pdf-sub-date">{new Date(sub.submitted_at).toLocaleString()}</span>
                          </div>
                          <div style={{ display: "flex", gap: "6px" }}>
                            <button
                              className={`ed-pdf-lock-btn ${sub.review_locked ? "locked" : ""}`}
                              title={sub.review_locked ? "Unlock review" : "Lock review"}
                              onClick={() => toggleSubmissionLock(sub)}
                            >
                              {sub.review_locked ? "🔒" : "🔓"}
                            </button>
                            <button 
                              className="ed-pdf-view-btn"
                              onClick={() => {
                                window.open(`/event/${eventId}/pdf-feedback/${sub.id}`, "_blank");
                              }}
                            >
                              👁️ View Feedback
                            </button>
                          </div>
                        </div>
                        <div className="ed-pdf-sub-preview">
                          <span className="ed-pdf-preview-stat">
                            📄 {sub.pages ? sub.pages.length : 0} pages reviewed
                          </span>
                          {sub.pages && sub.pages.filter(p => p.drawing_data && p.drawing_data.length > 0).length > 0 && (
                            <span className="ed-pdf-preview-stat">
                              🎨 {sub.pages.filter(p => p.drawing_data && p.drawing_data.length > 0).length} pages with drawings
                            </span>
                          )}
                          {sub.pages && sub.pages.filter(p => p.comment_text && p.comment_text.trim()).length > 0 && (
                            <span className="ed-pdf-preview-stat">
                              💬 {sub.pages.filter(p => p.comment_text && p.comment_text.trim()).length} pages with comments
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <button className="ed-modal-close" onClick={() => setShowPdfSubmissions(false)}>Close</button>
              </div>
            </div>
          )}

          {/* Selection Link Modal */}
          {showShareModal && (
            <div className="ed-modal-overlay" onClick={() => setShowShareModal(false)}>
              <div className="ed-modal" onClick={(e) => e.stopPropagation()}>
                <h3>Selection Link</h3>
                <p>Clients can view and vote on photos using this link:</p>
                {shareLink && (
                  <div className="ed-qr-container">
                    <QRCodeSVG
                      id="ed-selection-qr-svg"
                      value={shareLink}
                      size={200}
                      level="H"
                      includeMargin
                      bgColor="#ffffff"
                      fgColor="#14142b"
                    />
                  </div>
                )}
                <div className="ed-share-link-box">
                  <input type="text" value={shareLink} readOnly className="ed-share-input" />
                  <button className="ed-copy-btn" onClick={copyShareLink}>
                    Copy
                  </button>
                </div>
                <div className="ed-share-actions">
                  <button className="ed-download-qr-btn" onClick={() => downloadQrCode("ed-selection-qr-svg", "selection-qr")}>
                    Download QR
                  </button>
                  <button className="ed-modal-close" onClick={() => setShowShareModal(false)}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* QR Sharing Modal */}
          {showLiveShareModal && (
            <div className="ed-modal-overlay" onClick={() => setShowLiveShareModal(false)}>
              <div className="ed-modal" onClick={(e) => e.stopPropagation()}>
                <h3>QR Sharing</h3>
                <p>Guests scan this QR code and upload a selfie to instantly find their photos:</p>
                {liveShareLink && (
                  <div className="ed-qr-container ed-qr-container-live">
                    <QRCodeSVG
                      id="ed-live-qr-svg"
                      value={liveShareLink}
                      size={200}
                      level="H"
                      includeMargin
                      bgColor="#ffffff"
                      fgColor="#4ecdc4"
                    />
                  </div>
                )}
                <div className="ed-share-link-box">
                  <input type="text" value={liveShareLink} readOnly className="ed-share-input" />
                  <button className="ed-copy-btn ed-copy-btn-live" onClick={copyLiveShareLink}>
                    Copy
                  </button>
                </div>
                <div className="ed-share-actions">
                  <button className="ed-download-qr-btn ed-download-qr-btn-live" onClick={() => downloadQrCode("ed-live-qr-svg", "qr-sharing")}>
                    Download QR
                  </button>
                  <button className="ed-modal-close" onClick={() => setShowLiveShareModal(false)}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>

      {lightboxIndex >= 0 && (() => {
        const imgs = getLightboxImages();
        const photo = imgs[lightboxIndex];
        if (!photo) return null;
        return (
          <div className="ed-lightbox" onClick={closeLightbox}>
            {lightboxIndex > 0 && (
              <button className="ed-lb-arrow ed-lb-prev" onClick={(e) => { e.stopPropagation(); setLightboxIndex(lightboxIndex - 1); }}>‹</button>
            )}
            <div className="ed-lb-content" onClick={(e) => e.stopPropagation()}>
              <button className="ed-lb-close" onClick={closeLightbox}>✕</button>
              <img src={photo.image_url} alt={photo.original_filename || `Photo ${lightboxIndex + 1}`} className="ed-lb-image" />
              <div className="ed-lb-footer">
                <span className="ed-lb-counter">{lightboxIndex + 1} / {imgs.length}</span>
                {photo.original_filename && <span className="ed-lb-filename">{photo.original_filename}</span>}
              </div>
            </div>
            {lightboxIndex < imgs.length - 1 && (
              <button className="ed-lb-arrow ed-lb-next" onClick={(e) => { e.stopPropagation(); setLightboxIndex(lightboxIndex + 1); }}>›</button>
            )}
          </div>
        );
      })()}
    </div>
  );
}

export default EventDetails;
